import {
  AppstoreOutlined,
  AuditOutlined,
  BookOutlined,
  BuildOutlined,
  DatabaseOutlined,
  DownOutlined,
  HistoryOutlined,
  InfoCircleOutlined,
  LaptopOutlined,
  NotificationOutlined,
  RightOutlined,
  SettingOutlined,
  SwapOutlined,
  UserOutlined,
} from '@ant-design/icons';
import {
  Alert,
  App,
  Badge,
  Button,
  Dropdown,
  Popover,
  Result,
  Select,
  Tooltip,
} from 'antd';
import { type ReactNode, useEffect, useRef, useState } from 'react';
import { requestRoleViewSwitch } from '../../../api/active/roleViewEditorGuard';
import { useActiveSession } from '../../../api/active/sessionProvider';
import {
  type CatalogAuthority,
  canUseCatalogAction,
} from '../../../api/formal/phase1Permissions';
import { canReadOperationLogs } from '../../../audit-integration/operationLogAuthorization';
import { MarketingAnnouncementControlsProvider } from '../../../marketing-announcement/MarketingAnnouncementControls';
import { MarketingAnnouncementDialog } from '../../../marketing-announcement/MarketingAnnouncementDialog';
import {
  AppearanceSwitcher,
  useAppearancePreset,
} from '../../../shell/AppearanceProvider';
import {
  useShellPickerMotion,
  useShellViewMotion,
} from '../../../shell/AppShellMotion';
import motion from '../../../shell/AppShellMotion.module.css';
import { WorkspaceLogoutContext } from '../../../shell/WorkspaceShellActions';
import { PHASE1_DEPARTMENTS } from '../permission-center-ui/phase1PermissionMatrix';
import { useCpqShellController } from '../state/CpqContext';
import { isCasePreviewEnabled, isCaseView } from '../state/caseFeature';
import type { AppView } from '../state/types';
import styles from '../styles.module.css';
import { AdminConfigurationTutorial } from './AdminConfigurationTutorial';
import {
  type ConfigurationTutorialModule,
  configurationTutorialEvent,
  startConfigurationTutorial,
} from './configurationTutorial';
import { requestQuickStartTutorial } from './quickStartTutorialRequest';

interface NavItem {
  key: AppView;
  label: string;
  icon: ReactNode;
  capability?: readonly [featureId: string, actionId: string];
}

const navItems: NavItem[] = [
  {
    key: 'cases',
    label: '案例库',
    icon: <BookOutlined />,
    // Temporary frontend projection until a formal Case capability exists.
    capability: ['plans', 'plans:solution.read'],
  },
  {
    key: 'workspace',
    label: '报价工作台',
    icon: <AppstoreOutlined />,
    capability: ['workspace', 'workspace:quote.read'],
  },
  {
    key: 'quotes',
    label: '报价管理',
    icon: <HistoryOutlined />,
    capability: ['quotes', 'quotes:quote.read'],
  },
  {
    key: 'case-admin',
    label: '案例管理',
    icon: <BookOutlined />,
    // Temporary frontend projection until a formal Case capability exists.
    capability: ['plans', 'plans:solution.read'],
  },
  {
    key: 'plans',
    label: '方案管理',
    icon: <AppstoreOutlined />,
    capability: ['plans', 'plans:solution.read'],
  },
  {
    key: 'packages',
    label: '套餐管理',
    icon: <BuildOutlined />,
    capability: ['packages', 'packages:solution.read'],
  },
  {
    key: 'devices',
    label: '设备管理',
    icon: <LaptopOutlined />,
    capability: ['devices', 'devices:catalog.read'],
  },
  {
    key: 'replacements',
    label: '平替管理',
    icon: <SwapOutlined />,
  },
  {
    key: 'customers',
    label: '客户管理',
    icon: <UserOutlined />,
    capability: ['customers', 'customers:customer.read'],
  },
  {
    key: 'inventory',
    label: '库存管理',
    icon: <DatabaseOutlined />,
    capability: ['inventory', 'inventory:inventory.read'],
  },
  {
    key: 'marketing-announcement',
    label: '营销说明',
    icon: <NotificationOutlined />,
  },
  {
    key: 'settings',
    label: '系统设置',
    icon: <SettingOutlined />,
  },
  {
    key: 'operation-logs',
    label: '操作日志',
    icon: <AuditOutlined />,
  },
];

export function visibleNavigationItems(): readonly NavItem[] {
  return navItems.filter(
    (item) => !isCaseView(item.key) || isCasePreviewEnabled(),
  );
}

const FORMAL_VIEW_READ_CODES: Partial<Record<AppView, readonly string[]>> = {
  cases: ['solution.read'],
  'case-admin': ['solution.read'],
  workspace: ['quote.read'],
  quotes: ['quote.read'],
  plans: ['plan.read'],
  packages: ['package.read'],
  devices: ['equipment.read'],
  replacements: ['catalog.replacement.read'],
  customers: ['customer.read'],
  inventory: ['inventory.read'],
  'marketing-announcement': ['marketing.manage'],
  settings: ['settings.read'],
  permissions: ['settings.read'],
};

/** Direct routes and the sidebar use the same formal-session read gate. */
export function canViewInFormalSession(
  view: AppView,
  permissions: readonly string[] | undefined,
  _roleKeys: readonly string[] = [],
  scopeAuthority?: CatalogAuthority,
): boolean {
  if (isCaseView(view) && !isCasePreviewEnabled()) return false;
  if (scopeAuthority) {
    const module =
      view === 'devices'
        ? 'equipment'
        : view === 'packages'
          ? 'package'
          : view === 'plans'
            ? 'plan'
            : null;
    if (module) return canUseCatalogAction(scopeAuthority, module, 'read');
  }
  const granted = permissions ?? [];
  // 入口消费服务端动作投影；实际读取仍由后端核验管理员资格与日志范围。
  if (view === 'operation-logs') return canReadOperationLogs(granted);
  const readCodes = FORMAL_VIEW_READ_CODES[view];
  if (!readCodes) return true;
  if (view === 'permissions')
    return [
      'identity.user.read',
      'identity.role.read',
      'identity.user.manage',
      'identity.role.manage',
      'identity.department.manage',
      'identity.wecom.sync',
      'identity.assignment.read',
      'identity.assignment.manage',
      'identity.user.profile.manage',
      'identity.user.status.manage',
      'identity.session.revoke',
      'identity.role.policy.edit',
      'identity.role.policy.publish',
    ].some((code) => granted.includes(code));
  // 登录态由 shell 核验；所有成员都可进入个人页面 UI 偏好。
  // 公司配置和权限治理仍由设置页按各自权限过滤。
  if (view === 'settings') return true;
  return (
    readCodes.some((code) => granted.includes(code)) ||
    (view === 'replacements' && granted.includes('catalog.replacement.manage'))
  );
}

export function shellNavText(label: string): string {
  return label
    .replace('案例库', '案例')
    .replace('报价工作台', '方案')
    .replace('管理', '')
    .replace('工作台', '')
    .replace('库存', '仓储')
    .replace('系统设置', '设置')
    .replace('简化报价', '');
}

interface AppShellViewModel {
  offlineDemoEnabled: boolean;
  activeView: string | null;
  workspacePicker: 'devices' | 'packages' | 'plans' | 'temporary' | null;
  visibleItems: readonly NavItem[];
  showDeviceShortcut: boolean;
  showPackageShortcut: boolean;
  pendingReceiptQuotes: number;
  pendingInventoryReminders: number;
  roleValue?: string;
  roleOptions: { value: string; label: string }[];
  userValue?: string;
  userOptions: { value: string; label: string }[];
  roleViewEnabled: boolean;
  controlsDisabled: boolean;
  controlsDisabledHint?: string;
  roleViewHint?: string;
  roleViewActive?: boolean;
  canActivateView(view: string): boolean;
  activateDeviceShortcut(): void;
  activatePackageShortcut(): void;
  activateItem(item: NavItem, onActivated?: () => void): void;
  selectRole(value: string): void;
  selectUser(value: string): void;
  logout(): void;
}

function AppShellLayout({
  children,
  model,
}: {
  children: ReactNode;
  model: AppShellViewModel;
}) {
  const isRefinedAppearance = useAppearancePreset() !== 'purple';
  const [backendNavigationExpanded, setBackendNavigationExpanded] = useState(
    model.offlineDemoEnabled,
  );
  const [identityOpen, setIdentityOpen] = useState(false);
  const [configurationTutorialModule, setConfigurationTutorialModule] =
    useState<ConfigurationTutorialModule | null>(null);
  const mainContentRef = useShellViewMotion(model.activeView);
  useShellPickerMotion(mainContentRef, model.activeView, model.workspacePicker);
  const activeLabel =
    visibleNavigationItems().find((item) => item.key === model.activeView)
      ?.label ??
    (model.activeView === 'permissions' ? '系统设置' : '报价工作台');
  const roleLabel =
    model.roleOptions.find((option) => option.value === model.roleValue)
      ?.label ??
    model.roleValue ??
    '—';
  const userLabel =
    model.userOptions.find((option) => option.value === model.userValue)
      ?.label ??
    model.userValue ??
    '—';
  useEffect(() => {
    const onConfigurationTutorial = (event: Event) => {
      const module = (event as CustomEvent<{ module?: unknown }>).detail
        ?.module;
      if (module !== 'devices' && module !== 'packages' && module !== 'plans')
        return;
      const item = model.visibleItems.find(
        (candidate) => candidate.key === module,
      );
      if (!item || !model.canActivateView(module)) return;
      model.activateItem(item, () => setConfigurationTutorialModule(module));
    };
    window.addEventListener(
      configurationTutorialEvent,
      onConfigurationTutorial,
    );
    return () =>
      window.removeEventListener(
        configurationTutorialEvent,
        onConfigurationTutorial,
      );
  }, [model]);
  if (model.activeView === 'share') {
    return <div className={styles.publicRoot}>{children}</div>;
  }

  const renderNavigationItem = (item: NavItem) => (
    <Tooltip key={item.key} title={item.label} placement="right">
      <button
        type="button"
        aria-label={item.label}
        data-quick-start-module={item.key === 'workspace' ? 'plans' : undefined}
        aria-current={
          model.activeView === item.key &&
          (item.key !== 'workspace' ||
            model.workspacePicker === 'plans' ||
            model.workspacePicker === null)
            ? 'page'
            : undefined
        }
        className={`${styles.navButton} ${motion.navMotion} ${model.activeView === item.key && (item.key !== 'workspace' || model.workspacePicker === 'plans' || model.workspacePicker === null) ? motion.navSelected : ''} ${item.key === 'cases' ? styles.navCaseShortcut : ''} ${item.key === 'workspace' ? styles.navPlanShortcut : ''} ${model.activeView === item.key && (item.key !== 'workspace' || model.workspacePicker === 'plans' || model.workspacePicker === null) ? styles.navButtonActive : ''}`}
        disabled={!model.canActivateView(item.key)}
        onClick={() => model.activateItem(item)}
      >
        {item.icon}
        <span className={styles.navText}>{shellNavText(item.label)}</span>
        {item.key === 'quotes' && model.pendingReceiptQuotes > 0 ? (
          <Badge
            count={model.pendingReceiptQuotes}
            size="small"
            className={styles.navBadge}
          />
        ) : item.key === 'inventory' && model.pendingInventoryReminders > 0 ? (
          <Badge
            count={model.pendingInventoryReminders}
            size="small"
            className={styles.navBadge}
          />
        ) : null}
      </button>
    </Tooltip>
  );

  const caseItems = model.visibleItems.filter((item) => item.key === 'cases');
  const workspaceItems = model.visibleItems.filter(
    (item) => item.key === 'workspace',
  );
  const backendItems = model.visibleItems.filter(
    (item) =>
      item.key !== 'cases' &&
      item.key !== 'workspace' &&
      item.key !== 'settings' &&
      item.key !== 'operation-logs',
  );
  const settingsItems = model.visibleItems.filter(
    (item) => item.key === 'settings',
  );
  const operationLogItems = model.visibleItems.filter(
    (item) => item.key === 'operation-logs',
  );
  // 系统设置属于后台功能：与业务后台共用一个入口，收起时不再单独占一行。
  const backendGroupItems = [
    ...backendItems,
    ...settingsItems,
    ...operationLogItems,
  ];
  const activeBackendItem = backendGroupItems.find(
    (item) => item.key === model.activeView,
  );
  const backendNavigationLabel = backendNavigationExpanded
    ? '收起后台管理'
    : '展开后台管理';
  const collapsedBackendEntry =
    !backendNavigationExpanded && activeBackendItem
      ? activeBackendItem
      : undefined;

  return (
    <div className={styles.appRoot}>
      <aside className={styles.sidebar} aria-label="CPQ v2 主导航">
        <div className={styles.brand}>CPQ</div>
        <nav className={styles.navList}>
          {caseItems.map(renderNavigationItem)}
          {workspaceItems.map(renderNavigationItem)}
          {model.showPackageShortcut ? (
            <Tooltip title="报价套餐" placement="right">
              <button
                type="button"
                aria-label="报价套餐"
                data-quick-start-module="packages"
                aria-current={
                  model.activeView === 'workspace' &&
                  model.workspacePicker === 'packages'
                    ? 'page'
                    : undefined
                }
                className={`${styles.navButton} ${motion.navMotion} ${model.activeView === 'workspace' && model.workspacePicker === 'packages' ? motion.navSelected : ''} ${styles.navPackageShortcut} ${model.activeView === 'workspace' && model.workspacePicker === 'packages' ? styles.navButtonActive : ''}`}
                disabled={!model.canActivateView('workspace')}
                onClick={model.activatePackageShortcut}
              >
                <BuildOutlined />
                <span className={styles.navText}>套餐</span>
              </button>
            </Tooltip>
          ) : null}
          {model.showDeviceShortcut ? (
            <Tooltip title="报价设备" placement="right">
              <button
                type="button"
                aria-label="报价设备"
                data-quick-start-module="devices"
                aria-current={
                  model.activeView === 'workspace' &&
                  model.workspacePicker === 'devices'
                    ? 'page'
                    : undefined
                }
                className={`${styles.navButton} ${motion.navMotion} ${model.activeView === 'workspace' && model.workspacePicker === 'devices' ? motion.navSelected : ''} ${styles.navDeviceShortcut} ${model.activeView === 'workspace' && model.workspacePicker === 'devices' ? styles.navButtonActive : ''}`}
                disabled={!model.canActivateView('workspace')}
                onClick={model.activateDeviceShortcut}
              >
                <LaptopOutlined />
                <span className={styles.navText}>设备</span>
              </button>
            </Tooltip>
          ) : null}
          {backendGroupItems.length > 0 ? (
            <hr
              className={styles.navSectionDivider}
              aria-label="报价工作台与后台管理分隔线"
            />
          ) : null}
          {backendGroupItems.length > 0 ? (
            <Tooltip title={backendNavigationLabel} placement="right">
              <button
                type="button"
                aria-label={backendNavigationLabel}
                aria-expanded={backendNavigationExpanded}
                className={`${styles.navButton} ${motion.navMotion} ${activeBackendItem ? motion.navSelected : ''} ${activeBackendItem ? styles.navButtonActive : ''}`}
                onClick={() =>
                  setBackendNavigationExpanded((expanded) => !expanded)
                }
              >
                {collapsedBackendEntry ? (
                  collapsedBackendEntry.icon
                ) : backendNavigationExpanded ? (
                  <DownOutlined />
                ) : (
                  <RightOutlined />
                )}
                <span className={styles.navText}>
                  {collapsedBackendEntry
                    ? shellNavText(collapsedBackendEntry.label)
                    : '后台'}
                </span>
              </button>
            </Tooltip>
          ) : null}
          {backendGroupItems.length > 0 ? (
            <div
              className={`${motion.backendItemsDisclosure} ${backendNavigationExpanded ? motion.backendItemsDisclosureOpen : ''}`}
              aria-hidden={!backendNavigationExpanded}
              inert={!backendNavigationExpanded}
            >
              <div
                className={`${styles.navGroup} ${motion.backendItemsContent}`}
              >
                {backendItems.map(renderNavigationItem)}
                {settingsItems.length > 0 ? (
                  <hr className={styles.navGroupDivider} aria-hidden />
                ) : null}
                {settingsItems.map(renderNavigationItem)}
                {/* 操作日志与设置同级，紧随设置入口，仍受自身权限门控。 */}
                {operationLogItems.map(renderNavigationItem)}
              </div>
            </div>
          ) : null}
        </nav>
        <Popover
          trigger="click"
          placement="rightBottom"
          title={model.roleViewEnabled ? '角色切换' : '当前身份'}
          open={identityOpen}
          onOpenChange={setIdentityOpen}
          content={
            <div
              className={styles.identityPanel}
              role="dialog"
              aria-label="角色与成员"
              onKeyDown={(event) => {
                if (event.key === 'Escape') setIdentityOpen(false);
              }}
            >
              <div className={styles.identityField}>
                <span className={styles.identityLabel}>
                  {model.roleViewActive ? '测试视角' : '当前业务角色'}
                </span>
                {!model.roleViewEnabled ||
                (isRefinedAppearance && model.controlsDisabled) ? (
                  <span
                    className={styles.identityValue}
                    title={`当前角色：${roleLabel}`}
                  >
                    {roleLabel}
                  </span>
                ) : (
                  <Select
                    aria-label="切换角色"
                    value={model.roleValue}
                    options={model.roleOptions}
                    disabled={model.controlsDisabled}
                    onChange={model.selectRole}
                    style={{ width: '100%' }}
                  />
                )}
                {model.roleViewActive ? (
                  <Button
                    size="small"
                    disabled={model.controlsDisabled}
                    onClick={() => model.selectRole('original')}
                  >
                    退出角色测试
                  </Button>
                ) : null}
              </div>
              <div className={styles.identityField}>
                <span className={styles.identityLabel}>当前成员</span>
                {isRefinedAppearance ? (
                  <span
                    className={styles.identityValue}
                    title={`当前成员：${userLabel}`}
                  >
                    {userLabel}
                  </span>
                ) : (
                  <Select
                    aria-label="切换成员"
                    value={model.userValue}
                    options={model.userOptions}
                    disabled
                    onChange={model.selectUser}
                    style={{ width: '100%' }}
                  />
                )}
              </div>
              {model.roleViewHint ? (
                <span className={styles.identityHint}>
                  {model.roleViewHint}
                </span>
              ) : null}
              {model.controlsDisabledHint ? (
                <span className={styles.identityHint}>
                  {model.controlsDisabledHint}
                </span>
              ) : null}
            </div>
          }
        >
          <button
            type="button"
            className={styles.identityTrigger}
            aria-label={model.roleViewEnabled ? '角色切换' : '当前身份'}
            aria-haspopup="dialog"
            aria-expanded={identityOpen}
            title={`${model.roleViewEnabled ? '角色切换' : '当前身份'} · ${roleLabel} · ${userLabel}`}
            onKeyDown={(event) => {
              if (event.key === 'Escape') setIdentityOpen(false);
            }}
          >
            <span className={styles.sidebarBottom} aria-hidden="true">
              管
            </span>
          </button>
        </Popover>
      </aside>
      <section className={styles.pageFrame}>
        {model.activeView !== 'workspace' ||
        !model.canActivateView('workspace') ? (
          <header className={styles.topbar}>
            <div>
              <span className={styles.crumb}>
                {isRefinedAppearance
                  ? activeLabel
                  : `${activeLabel} / ${activeLabel}`}
              </span>
            </div>
            <div className={styles.topbarActions}>
              <Dropdown
                trigger={['click']}
                menu={{
                  items: [
                    {
                      key: 'quick-start',
                      label: '快速开始',
                      disabled: !model.canActivateView('workspace'),
                    },
                    {
                      key: 'field-explanation',
                      label: '字段解释',
                      disabled: !model.canActivateView('workspace'),
                    },
                    {
                      key: 'configuration',
                      label: '后台配置',
                      children: [
                        { key: 'configuration-devices', label: '设备管理' },
                        { key: 'configuration-packages', label: '套餐管理' },
                        { key: 'configuration-plans', label: '方案管理' },
                      ],
                    },
                  ],
                  onClick: ({ key }) => {
                    if (key === 'quick-start' || key === 'field-explanation') {
                      const start = () => requestQuickStartTutorial(key);
                      if (model.activeView === 'workspace') {
                        start();
                        return;
                      }
                      const workspaceItem = model.visibleItems.find(
                        (item) => item.key === 'workspace',
                      );
                      if (!workspaceItem || !model.canActivateView('workspace'))
                        return;
                      model.activateItem(workspaceItem, start);
                      return;
                    }
                    const module = key.slice('configuration-'.length);
                    if (
                      module === 'devices' ||
                      module === 'packages' ||
                      module === 'plans'
                    )
                      startConfigurationTutorial(module);
                  },
                }}
              >
                <Button
                  type="text"
                  size="small"
                  aria-label="新手教程"
                  aria-haspopup="menu"
                >
                  新手教程 <DownOutlined />
                </Button>
              </Dropdown>
              {isCasePreviewEnabled() && model.activeView === 'cases' ? (
                <Popover
                  trigger="click"
                  motion={{ motionName: '' }}
                  destroyOnHidden
                  title="案例模块演示说明"
                  content={
                    <p style={{ maxWidth: 320, margin: 0 }}>
                      {model.offlineDemoEnabled
                        ? '本地演示：目录使用本机数据快照，新增与修改保存在独立演示区。下单、通知、同步和识别均为模拟结果。'
                        : '案例卡片、方案关联和客户分享为前端演示状态；正式选品目录以当前环境接口返回为准。'}
                    </p>
                  }
                >
                  <Button
                    type="text"
                    icon={<InfoCircleOutlined />}
                    aria-label="查看案例演示说明"
                  >
                    演示说明
                  </Button>
                </Popover>
              ) : null}
              <AppearanceSwitcher />
              <Button onClick={model.logout}>退出登录</Button>
            </div>
          </header>
        ) : null}
        <WorkspaceLogoutContext.Provider value={model.logout}>
          {model.offlineDemoEnabled ? (
            <Alert
              type="info"
              showIcon
              title="本地全功能演示 · 修改仅保存在本机，下单和外部通知均为模拟"
            />
          ) : null}
          <main ref={mainContentRef} className={styles.mainContent}>
            {children}
          </main>
        </WorkspaceLogoutContext.Provider>
        <AdminConfigurationTutorial
          module={configurationTutorialModule}
          onClose={() => setConfigurationTutorialModule(null)}
        />
      </section>
    </div>
  );
}

export function AppShell({
  children,
  publicAccess = false,
}: {
  children: ReactNode;
  publicAccess?: boolean;
}) {
  return publicAccess ? (
    <div className={styles.publicRoot}>{children}</div>
  ) : (
    <InternalAppShell>{children}</InternalAppShell>
  );
}

function InternalAppShell({ children }: { children: ReactNode }) {
  const controller = useCpqShellController();
  const session = useActiveSession();
  const { modal, message } = App.useApp();
  const [announcementOpen, setAnnouncementOpen] = useState(true);
  const activateWithCaseAdminGuard = (
    targetView: string,
    proceed: () => unknown,
  ) => {
    if (controller.activeView === 'case-admin' && targetView !== 'case-admin') {
      const event = new CustomEvent('cpq-case-admin-before-leave', {
        cancelable: true,
        detail: { proceed },
      });
      if (!window.dispatchEvent(event)) return;
    }
    return proceed();
  };
  const identity =
    session.state.status === 'ready' ? session.state.user : undefined;
  const visibleItems = visibleNavigationItems().filter(
    (item) =>
      session.offlineDemoEnabled ||
      Boolean(
        identity &&
          canViewInFormalSession(
            item.key,
            identity.permissions,
            identity.roles,
            identity,
          ),
      ),
  );
  const originalIdentity = useRef<{ owner: string; label: string } | null>(
    null,
  );
  const identityOwner = identity
    ? `${identity.companyId}:${identity.id}`
    : null;
  const actualIdentityLabel = identity?.roleView
    ? originalIdentity.current?.owner === identityOwner
      ? originalIdentity.current.label
      : '本人：真实身份'
    : identity?.roles.some((role) =>
          ['company_superadmin', 'fixed_superadmin'].includes(role),
        )
      ? '本人：超级管理员'
      : '本人：管理员';
  // 仅保留当前登录主体的展示文案；预览角色不代表真实管理身份，也不参与鉴权。
  useEffect(() => {
    if (!identityOwner) originalIdentity.current = null;
    else if (!identity?.roleView)
      originalIdentity.current = {
        owner: identityOwner,
        label: actualIdentityLabel,
      };
  }, [identityOwner, identity?.roleView, actualIdentityLabel]);
  const canActivateView = (view: AppView) =>
    session.offlineDemoEnabled ||
    Boolean(
      identity &&
        canViewInFormalSession(
          view,
          identity.permissions,
          identity.roles,
          identity,
        ),
    );
  const model: AppShellViewModel = {
    offlineDemoEnabled: session.offlineDemoEnabled,
    activeView: controller.activeView,
    workspacePicker: controller.workspacePicker,
    visibleItems,
    showDeviceShortcut:
      session.offlineDemoEnabled ||
      Boolean(
        canUseCatalogAction(identity, 'equipment', 'read') &&
          identity?.permissions.includes('quote.read'),
      ),
    showPackageShortcut:
      session.offlineDemoEnabled ||
      Boolean(
        canUseCatalogAction(identity, 'package', 'read') &&
          identity?.permissions.includes('quote.read'),
      ),
    pendingReceiptQuotes: controller.pendingReceiptQuotes,
    // The warehouse page is now a real read-only projection. Legacy local
    // reminder counts must not appear as formal inventory facts.
    pendingInventoryReminders: 0,
    roleValue: identity?.roleViewEnabled
      ? (identity.roleView?.roleId ?? 'original')
      : identity?.roles[0],
    roleOptions: identity?.roleViewEnabled
      ? [
          { value: 'original', label: actualIdentityLabel },
          // 候选由服务端读取，选角再由/me校验发布状态和权限交集。
          ...(session.roleViewOptions ?? [])
            .filter(
              (role) =>
                role.authorizationSource === 'model3' ||
                PHASE1_DEPARTMENTS.some((entry) => entry.roleKey === role.key),
            )
            .map((role) => ({ value: role.id, label: `预览：${role.name}` })),
        ]
      : (identity?.roles.map((role) => ({ value: role, label: role })) ?? []),
    userValue: identity?.id,
    userOptions: identity
      ? [{ value: identity.id, label: identity.displayName }]
      : [],
    roleViewEnabled: identity?.roleViewEnabled === true,
    controlsDisabled:
      identity?.roleViewEnabled !== true || Boolean(session.roleViewSwitching),
    controlsDisabledHint:
      identity?.roleViewEnabled === true && session.roleViewSwitching
        ? '正在切换角色，请稍候'
        : undefined,
    roleViewHint:
      session.roleViewError ??
      (identity?.roleViewEnabled
        ? identity.roleView
          ? '正在按所选角色测试操作，成员授权未改变。退出后恢复本人权限。'
          : '当前使用本人权限。选择角色后可测试对应操作，不改变成员授权。'
        : undefined),
    roleViewActive: Boolean(identity?.roleView),
    canActivateView: (view) => {
      return canActivateView(view as AppView);
    },
    activateDeviceShortcut: () =>
      activateWithCaseAdminGuard(
        'workspace',
        controller.activateDeviceShortcut,
      ),
    activatePackageShortcut: () =>
      activateWithCaseAdminGuard(
        'workspace',
        controller.activatePackageShortcut,
      ),
    activateItem: (item, onActivated) =>
      activateWithCaseAdminGuard(item.key, () => {
        const activation = controller.activateView(item.key);
        if (onActivated)
          void activation.then((activated) => {
            if (activated) onActivated();
          });
        return activation;
      }),
    selectRole: (value) => {
      const guard = requestRoleViewSwitch();
      if (!guard.allowed) {
        message.warning(
          `正在编辑${guard.blockedEditors.join('、')}，请先保存或关闭编辑窗口后再切换角色。`,
        );
        return;
      }
      const roleId = value === 'original' ? null : value;
      const hasDirtyDraft =
        controller.hasUnsavedFormalWorkspaceDrafts(identity);
      const proceed = () =>
        session
          .selectRoleView(roleId, async () => {
            const checkpoint =
              await controller.ensureFormalWorkspaceRecovery(true);
            if (!checkpoint)
              throw new Error(
                '当前工作区恢复点尚未保存，请先处理保存提示，再切换角色。',
              );
            return () => {
              if (!controller.isFormalWorkspaceRecoveryCurrent(checkpoint))
                throw new Error(
                  '工作区内容在保存期间发生变化，已保留当前角色和编辑内容，请重试切换。',
                );
            };
          })
          .catch((error) => {
            message.error(
              error instanceof Error ? error.message : '角色切换失败',
            );
          });
      if (hasDirtyDraft) {
        modal.confirm({
          title: '当前报价有未保存改动',
          content:
            '确认切换前会保存当前角色的本地草稿；保存失败将留在当前角色。切回后可以继续编辑。是否切换？',
          okText: '保留草稿并切换',
          cancelText: '继续编辑',
          onOk: () => activateWithCaseAdminGuard('role-view', proceed),
        });
      } else activateWithCaseAdminGuard('role-view', proceed);
    },
    selectUser: () => undefined,
    logout: () =>
      activateWithCaseAdminGuard('logout', () => void session.logout()),
  };
  const viewAllowed =
    session.offlineDemoEnabled ||
    (identity &&
      canViewInFormalSession(
        controller.activeView,
        identity.permissions,
        identity.roles,
        identity,
      ));
  return (
    <MarketingAnnouncementControlsProvider
      openAnnouncement={() => setAnnouncementOpen(true)}
    >
      <AppShellLayout model={model}>
        {viewAllowed ? (
          children
        ) : (
          <Result status="403" title="没有访问此模块的权限" />
        )}
      </AppShellLayout>
      <MarketingAnnouncementDialog
        open={announcementOpen && !session.offlineDemoEnabled}
        onClose={() => setAnnouncementOpen(false)}
      />
    </MarketingAnnouncementControlsProvider>
  );
}
